import React, { useEffect, useState } from "react";
import { 
  Box, 
  Typography, 
  Button, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  Divider, 
  MenuItem, 
  Select, 
  SelectChangeEvent,
  Card,
  CardContent,
  Grid,
  useTheme,
  alpha,
  Chip,
  Avatar
} from "@mui/material";
import { ContactSubmission } from "../../../types/contactSubmission";
import { contactService } from "../../../services/api/contactService";
import { toast } from "react-toastify";
import { useAuth } from "../../../contexts/AuthContext";

// Icons
import EmailIcon from "@mui/icons-material/Email";
import PersonIcon from "@mui/icons-material/Person";
import SubjectIcon from "@mui/icons-material/Subject";
import MessageIcon from "@mui/icons-material/Message";
import EventIcon from "@mui/icons-material/Event";
import UpdateIcon from "@mui/icons-material/Update";
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface ContactViewModalProps {
    open: boolean;
    contact?: ContactSubmission | null;
    onClose: () => void;
    onUpdateStatus?: (id: string, newStatus: ContactSubmission["status"]) => void;
}

const STATUS_OPTIONS: ContactSubmission["status"][] = ["New", "In Progress", "Resolved"];

const ContactViewModal: React.FC<ContactViewModalProps> = ({
  open,
  contact,
  onClose,
  onUpdateStatus,
}) => {
  const { tokens } = useAuth();
  const [selectedStatus, setSelectedStatus] = useState<ContactSubmission["status"]>("New");
  const theme = useTheme();

  // Ensure the dropdown is preselected with the correct status
  useEffect(() => {
    if (contact?.status && STATUS_OPTIONS.includes(contact.status)) {
      setSelectedStatus(contact.status);
    } else {
      setSelectedStatus("New"); // Default to "New" if status is missing or not valid
    }
  }, [contact]);

  // Handle status change
  const handleStatusChange = async (event: SelectChangeEvent<string>) => {
    const newStatus = event.target.value as ContactSubmission["status"];
    try {
      if (!tokens?.idToken) throw new Error("User is not authenticated");

      await contactService.updateContact(tokens.idToken, contact!.id, { status: newStatus });

      toast.success(`Status updated to ${newStatus}`);
      setSelectedStatus(newStatus);
      onUpdateStatus && onUpdateStatus(contact!.id, newStatus);
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status.");
    }
  };

  if (!contact) return null;

  // Select appropriate status icon
  const getStatusIcon = () => {
    switch(selectedStatus) {
      case "New":
        return <MarkEmailUnreadIcon />;
      case "In Progress":
        return <UpdateIcon />;
      case "Resolved":
        return <MarkEmailReadIcon />;
      default:
        return <MarkEmailUnreadIcon />;
    }
  };

  // Get status color
  const getStatusColor = () => {
    switch(selectedStatus) {
      case "New":
        return theme.palette.warning;
      case "In Progress":
        return theme.palette.info;
      case "Resolved":
        return theme.palette.success;
      default:
        return theme.palette.warning;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: theme.shadows[10]
        }
      }}
    >
      <DialogTitle sx={{ 
        p: 0,
        position: 'relative',
        height: 100,
        bgcolor: alpha(getStatusColor().main, 0.2),
        display: 'flex',
        alignItems: 'flex-end'
      }}>
        <Box sx={{ p: 2, pl: 3, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5" fontWeight={600}>
            Contact Submission Details
          </Typography>
          <Chip
            label={selectedStatus}
            color={
              selectedStatus === "New" 
                ? "warning" 
                : selectedStatus === "In Progress" 
                  ? "info" 
                  : "success"
            }
            icon={getStatusIcon()}
            sx={{ fontWeight: 500 }}
          />
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3, mt: 1 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom color="primary">
                  Contact Information
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 3 }}>
                  <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, mr: 2 }}>
                    <PersonIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Name</Typography>
                    <Typography variant="body1" fontWeight={500}>{contact.name || "Not provided"}</Typography>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, mr: 2 }}>
                    <EmailIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Email</Typography>
                    <Typography variant="body1" fontWeight={500}>{contact.email || "Not provided"}</Typography>
                  </Box>
                </Box>
                
                {contact.subject && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, mr: 2 }}>
                      <SubjectIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Subject</Typography>
                      <Typography variant="body1" fontWeight={500}>{contact.subject}</Typography>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom color="primary">
                  Submission Details
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 3 }}>
                  <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main, mr: 2 }}>
                    <EventIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Created At</Typography>
                    <Typography variant="body1" fontWeight={500}>{new Date(contact.createdAt).toLocaleString()}</Typography>
                  </Box>
                </Box>
                
                {contact.updatedAt && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main, mr: 2 }}>
                      <UpdateIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Updated At</Typography>
                      <Typography variant="body1" fontWeight={500}>{new Date(contact.updatedAt).toLocaleString()}</Typography>
                    </Box>
                  </Box>
                )}
                
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>Status</Typography>
                  <Select
                    value={selectedStatus}
                    onChange={handleStatusChange}
                    fullWidth
                    sx={{ 
                      borderRadius: 2,
                      '.MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(getStatusColor().main, 0.5),
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: getStatusColor().main,
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: getStatusColor().main,
                      },
                    }}
                  >
                    {STATUS_OPTIONS.map((status) => (
                      <MenuItem key={status} value={status}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {status === "New" && <MarkEmailUnreadIcon fontSize="small" sx={{ mr: 1, color: theme.palette.warning.main }} />}
                          {status === "In Progress" && <UpdateIcon fontSize="small" sx={{ mr: 1, color: theme.palette.info.main }} />}
                          {status === "Resolved" && <CheckCircleIcon fontSize="small" sx={{ mr: 1, color: theme.palette.success.main }} />}
                          {status}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          {contact.message && (
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <MessageIcon sx={{ mr: 1, color: theme.palette.text.secondary }} />
                    <Typography variant="h6" fontWeight={600} color="primary">Message</Typography>
                  </Box>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      p: 2, 
                      borderRadius: 1, 
                      bgcolor: alpha(theme.palette.background.default, 0.5),
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      whiteSpace: 'pre-wrap'
                    }}
                  >
                    {contact.message}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1, justifyContent: 'flex-end' }}>
        <Button 
          variant="outlined" 
          onClick={onClose}
          sx={{ borderRadius: 2, px: 3 }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactViewModal;
